import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { callMsGraph } from "../graph";
import { fetchWithRetry } from "../utils/graphApi";
import Header from "./Header";
import Sidebar from "./Sidebar";
import MobileHeader from "./MobileHeader";
import ProjectDetails from "./ProjectDetails";
import FinancialPerformance from "./FinancialPerformance";
import Scheduling from "./Scheduling";
import ProductionPerformance from "./ProductionPerformance";
import TeamsChat from "./TeamsChat";
import ProjectEmails from "./ProjectEmails";
import ProjectTeam from "./ProjectTeam";
import ScopeDescription from "./ScopeDescription";
import { Contacts } from "./contacts/Contacts";
import { ManageContacts } from "./contacts/ManageContacts";
import { ManageProject } from './projects/ManageProject';
import ProjectArchive from './projects/ProjectArchive';
import AccountManagement from './AccountManagement';
import InventoryManagement from './InventoryManagement';
import EquipmentManagement from './EquipmentManagement';
import ConfigurationPage from './ConfigurationPage';
import { useAppRole } from '../context/UserRoleContext';
import { Link24Regular } from "@fluentui/react-icons";
import "./ProjectDashboard.css";
import axios from "axios";
import { authenticatedFetch } from "../utils/authApi";
import { useAuthFetch } from "../hooks/useAuthFetch";
import {
  PRODUCTION_PERFORMANCE_SECTION_ID,
  scrollToProductionPerformance,
} from "../utils/pwaUtils";
import { useStandalonePwa } from "../hooks/useStandalonePwa";
import PwaGlobalSearch from "./PwaGlobalSearch";
import { GlobalSearch } from "./SearchBar";
import ElastizellDailyReport from "./ElastizellDailyReport";
import ProjectTemplateReport from "./ProjectTemplateReport";
import { useCompactNav } from "../utils/compactNav";

function ProjectDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { projectId } = useParams();
  const isDailyReport = searchParams.get("view") === "daily-report";
  const isTemplateReport = searchParams.get("view") === "report";
  const reportTemplateId = isTemplateReport ? searchParams.get("template") || "" : "";
  const requestedOpportunityId = searchParams.get("opportunityId") || "";
  const { instance, accounts } = useMsal();
  const { getAuthConfig, isReady } = useAuthFetch();
  const { isLoaded: isRoleLoaded } = useAppRole();
  
  const isContactsPage = location.pathname.includes('/contacts');
  const isAccountsPage = location.pathname === '/accounts';
  const isInventoryPage = location.pathname === '/inventory';
  const isEquipmentPage = location.pathname === '/equipment';
  const isArchivePage = location.pathname === '/archive';
  const isProjectListPage = location.pathname === '/';
  const isConfigurationPage = location.pathname === '/configuration' || location.pathname.startsWith('/configuration/');
  const isStockInventoryManagePage = location.pathname === '/configuration/stock-inventory';
  const isLaborScopeManagePage = location.pathname === '/configuration/labor-scope-codes';
  const isEquipmentManagePage = location.pathname === '/configuration/equipment-time-cards';
  const isPpDataManagePage = location.pathname === '/configuration/projects-production-performance-data';
  const isCatalogManagePage = isStockInventoryManagePage || isLaborScopeManagePage || isEquipmentManagePage || isPpDataManagePage;
  
  // Pages that should hide the sidebar
  const shouldHideSidebar = isProjectListPage || isContactsPage || isAccountsPage || isArchivePage || isInventoryPage || isEquipmentPage || isConfigurationPage;
  const [isProjectSelected, setIsProjectSelected] = useState(() => 
    localStorage.getItem('isProjectSelected') === 'true'
  );

  const [projectData, setProjectData] = useState(() => {
    const savedDetails = localStorage.getItem('selectedProjectDetails');
    return savedDetails ? JSON.parse(savedDetails) : {};
  });
  const reportProjectReady = !requestedOpportunityId || projectData?.id_project === requestedOpportunityId;
  const loadingRecentId = useRef("");

  const [performanceMetrics, setPerformanceMetrics] = useState({
    billingProgress: 53.7,
    costPerformance: 53.7,
  });

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const isMobile = useCompactNav();
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
  const [namePinned, setNamePinned] = useState(false);
  const projectDetailsRef = useRef(null);
  const isStandalonePwa = useStandalonePwa();
  
  // User profile data
  const [userProfile, setUserProfile] = useState(null);
  const [profileImage, setProfileImage] = useState(null);

  const [metrics, setMetrics] = useState([
    { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
    { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
    { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
  ]);
  const [milesInfo, setMilesInfo] = useState({ kansas: 257, waller: 498 }); // Example static values

  function toggleSidebar() {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  }

  function toggleMobileMenu() {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  }

  function closeMobileMenu() {
    setIsMobileMenuOpen(false);
  }

  // Close drawer when navigating (prevents stuck overlay on iOS)
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const showSidebar = (!shouldHideSidebar || isMobile) && (!isMobile || isMobileMenuOpen);
  useEffect(() => {
    if (!isMobile) setIsMobileMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    const node = projectDetailsRef.current;
    if (!node || !isProjectSelected || isDailyReport || isTemplateReport) {
      setNamePinned(false);
      return;
    }

    const update = () => {
      const current = projectDetailsRef.current;
      if (!current) return;
      const rect = current.getBoundingClientRect();
      if (rect.height === 0) {
        setNamePinned(false);
        return;
      }
      const header = document.querySelector(".desktop-header, .mobile-header-container");
      const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      setNamePinned(rect.bottom <= headerBottom);
    };

    update();
    window.addEventListener("scroll", update, { passive: true, capture: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, { capture: true });
      window.removeEventListener("resize", update);
    };
  }, [isProjectSelected, isDailyReport, isTemplateReport, projectData?.projectId, projectData?.projectName]);

  // Fetch user profile data
  useEffect(() => {
    if (accounts.length > 0) {
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then((response) => {
          callMsGraph(response.accessToken).then((data) => {
            setUserProfile(data);
          });

          // Fetch profile photo with throttling support
          fetchWithRetry(
            "https://graph.microsoft.com/v1.0/me/photo/$value",
            { headers: { Authorization: `Bearer ${response.accessToken}` } },
            3, // retries
            false // don't cache blob responses
          )
            .then((res) => {
              if (!res || !res.ok) throw new Error("No profile photo");
              return res.blob();
            })
            .then((blob) => {
              setProfileImage(URL.createObjectURL(blob));
            })
            .catch(() => {
              setProfileImage(null);
            });
        })
        .catch((error) => console.error("Error fetching profile data:", error));
    }
  }, [accounts, instance]);

  // Helper function to format address
  const formatAddress = useCallback((address) => {
    if (!address) return '-';
    
    const parts = [];
    if (address.Street) parts.push(address.Street);
    if (address.City) parts.push(address.City);
    if (address.State) parts.push(address.State);
    if (address.ZipCode) parts.push(address.ZipCode);
    
    return parts.length > 0 ? parts.join(', ') : '-';
  }, []);

  // Function to handle project selection
  const handleProjectSelect = useCallback(async (id) => {
    if (!accounts.length) return;

    try {
      const opportunityResponse = await authenticatedFetch(
        instance,
        accounts[0],
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/opportunity/${id}`
      );
      if (!opportunityResponse.ok) {
        throw new Error(`Failed to load opportunity (${opportunityResponse.status})`);
      }
      const opportunityData = await opportunityResponse.json();
      const projectRecord = opportunityData.records[0];

      const projectInfoResponse = await authenticatedFetch(
        instance,
        accounts[0],
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/projects/${projectRecord.Project__c}/Info`
      );
      if (!projectInfoResponse.ok) {
        throw new Error(`Failed to load project info (${projectInfoResponse.status})`);
      }
      const projectInfoData = await projectInfoResponse.json();
      
      // Info API may put fields at root or under records
      const projectInfoFields = projectInfoData?.records && typeof projectInfoData.records === 'object'
        ? projectInfoData.records
        : (projectInfoData || {});
      const projectRecordProject = projectRecord.Project__r || {};
      const infoAddress = projectInfoFields.Address || projectInfoData?.Address || {};
      const street = projectRecord.Street_Project__c || projectRecordProject.Street__c || infoAddress.Street || '';
      const city = projectRecord.City_Project__c || projectRecordProject.gmb_City__c || infoAddress.City || '';
      const state = projectRecord.State_Project__c || projectRecordProject.gmb_Project_State__c || infoAddress.State || '';
      const zipCode = projectRecord.Zip_Code_Project__c || projectRecordProject.Zip_Code__c || infoAddress.ZipCode || '';
      const composedAddress = formatAddress({ Street: street, City: city, State: state, ZipCode: zipCode });
      const infoFormatted = formatAddress(infoAddress);
      const projectAddress = composedAddress !== '-' ? composedAddress : (infoFormatted !== '-' ? infoFormatted : '');

      const infoValue = (key) => projectInfoFields[key] || projectInfoData?.[key] || '-';

      // Store all project details including project ID and division
      
      const projectDetails = {
        id_project: projectRecord.Id,
        company: projectRecord.Company__c,
        projectNo: projectRecord.Project__r?.Project_System_Wide_ID__c,
        opportunityNo: projectRecord.Opportunity_System_Wide_ID__c,
        projectName: projectRecord.Project__r?.Name,
        scope: projectRecord.dv_Type__c,
        deliveryMethod: projectRecord.Delivery_Method__c,
        scopeDescription: projectRecord.Project_Scope__c,
        projectStatus: projectRecord.Project__r?.Project_Status__c || '-',
        billingDate: infoValue('bill_date'),
        contract: infoValue('contract'),

        street,
        city,
        state,
        zipCode,

        Address: (infoAddress.Street || street) ? {
          Street: street,
          City: city,
          State: state,
          ZipCode: zipCode,
        } : infoAddress,
        projectAddress,

        customerName: projectRecord.Contract_With__r?.Name || '-',
        customerStreet: projectRecord.Contract_With__r?.BillingStreet || '',
        customerCity: projectRecord.Contract_With__r?.BillingCity || '',
        customerState: projectRecord.Contract_With__r?.BillingState || '',
        customerZipCode: projectRecord.Contract_With__r?.BillingPostalCode || '',

        opportunityName: projectRecord.Name,
        opportunityUUID: projectRecord.Opportunity_System_Wide_ID__c,
        projectId: projectRecord.Project__r?.Project_System_Wide_ID__c,
        division: projectRecord.dv_Division_New__c,
        salesforceProjectId: projectRecord.Project__c,
        estimatingFolderLink: projectRecord.Project__r?.Nettles_Estimating_Folder_Link__c || null,
        sharepointItemId: projectRecord.Project__r?.Sharepoint_Item_ID_Project__c || null,

        taxExempt: infoValue('tax_exempt'),
        certifiedPayroll: infoValue('certified_payroll'),
        ppBonds: infoValue('p_and_p_bonds'),
        ccip: infoValue('ccip'),
        retainage: infoValue('retainage'),
        prevailingWage: infoValue('prevailing_wage'),
        bill_date: infoValue('bill_date'),
        tax_exempt: infoValue('tax_exempt'),
        certified_payroll: infoValue('certified_payroll'),
        p_and_p_bonds: infoValue('p_and_p_bonds'),
        prevailing_wage: infoValue('prevailing_wage')
      };
      console.log('---',projectDetails)
      localStorage.setItem('selectedProjectDetails', JSON.stringify(projectDetails));
      localStorage.setItem('isProjectSelected', 'true');

      // Dispatch event with project details
      const event = new CustomEvent('projectDetailsChanged', {
        detail: projectDetails
      });
      window.dispatchEvent(event);
      setProjectData(projectDetails);  // update state immediately


      // Do NOT update the URL to project number here
      // Only update the URL if the opportunityNo is different (e.g., when selecting a new project from a list)
      // navigate(`/project/${projectRecord.Project__r?.Project_System_Wide_ID__c}`);
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  }, [navigate, formatAddress, instance, accounts]);

  useEffect(() => {
    if (!requestedOpportunityId || !(isDailyReport || isTemplateReport)) return undefined;
    if (projectData?.id_project === requestedOpportunityId) {
      const next = new URLSearchParams(searchParams);
      next.delete("opportunityId");
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
      return undefined;
    }
    if (loadingRecentId.current === requestedOpportunityId) return undefined;
    loadingRecentId.current = requestedOpportunityId;
    handleProjectSelect(requestedOpportunityId).finally(() => {
      if (loadingRecentId.current === requestedOpportunityId) loadingRecentId.current = "";
    });
    return undefined;
  }, [requestedOpportunityId, isDailyReport, isTemplateReport, projectData?.id_project, handleProjectSelect, navigate, location.pathname, searchParams]);

  // Effect to handle project ID from URL
  useEffect(() => {
    if (projectId && (!isProjectSelected || projectData.opportunityNo !== projectId)) {
      // Find the project in the projects list
      const projects = JSON.parse(localStorage.getItem('projectsList') || '[]');
      const project = projects.find(p => p.Opportunity_System_Wide_ID__c === projectId);
      
      if (project) {
        handleProjectSelect(project.Id);
        // Dispatch events to refresh all components
        window.dispatchEvent(new CustomEvent('refreshContacts'));
        window.dispatchEvent(new CustomEvent('refreshTeamsChat'));
        window.dispatchEvent(new CustomEvent('refreshProjectEmails'));
      }
    }
  }, [projectId]);

  // Add effect to handle project data changes
  useEffect(() => {
    if (projectData?.opportunityUUID) {
      // Dispatch events to refresh all components when project data changes
      window.dispatchEvent(new CustomEvent('refreshContacts'));
      window.dispatchEvent(new CustomEvent('refreshTeamsChat'));
      window.dispatchEvent(new CustomEvent('refreshProjectEmails'));
      
      // Scroll to top when project data loads
      if (location.pathname === '/dashboard') {
        window.scrollTo(0, 0);
        const mainContent = document.querySelector('.dashboard-content');
        if (mainContent) {
          mainContent.scrollTop = 0;
        }
      }
    }
  }, [projectData, location.pathname]);

  useEffect(() => {
    const handleProjectDetailsChange = (event) => {
      const detail = event.detail;
      if (!detail || typeof detail !== 'object') {
        return;
      }
      setProjectData(detail);
      setIsProjectSelected(true);
    };

    const handleProjectDataCleared = () => {
      setProjectData(null);
      setIsProjectSelected(false);
    };

    const handleLoadRecentDashboard = async (event) => {
      const { opportunityId } = event.detail;
      if (opportunityId) {
        // Ensure isProjectSelected is set before loading
        localStorage.setItem('isProjectSelected', 'true');
        setIsProjectSelected(true);
        
        await handleProjectSelect(opportunityId);
        // Dispatch events to refresh all components
        window.dispatchEvent(new CustomEvent('refreshContacts'));
        window.dispatchEvent(new CustomEvent('refreshTeamsChat'));
        window.dispatchEvent(new CustomEvent('refreshProjectEmails'));
        
        // Scroll to top after loading project
        setTimeout(() => {
          window.scrollTo(0, 0);
          const mainContent = document.querySelector('.dashboard-content');
          if (mainContent) {
            mainContent.scrollTop = 0;
          }
        }, 100);
      }
    };

    window.addEventListener('projectDetailsChanged', handleProjectDetailsChange);
    window.addEventListener('projectDataCleared', handleProjectDataCleared);
    window.addEventListener('loadRecentDashboard', handleLoadRecentDashboard);

    return () => {
      window.removeEventListener('projectDetailsChanged', handleProjectDetailsChange);
      window.removeEventListener('projectDataCleared', handleProjectDataCleared);
      window.removeEventListener('loadRecentDashboard', handleLoadRecentDashboard);
    };
  }, [handleProjectSelect]);

  useEffect(() => {
    // Check localStorage directly to handle recent dashboard clicks
    const isProjectSelectedInStorage = localStorage.getItem('isProjectSelected') === 'true';
    
    // If trying to access dashboard without selected project, redirect to home
    // But only if we're not loading a recent dashboard (check for loadRecentDashboard event)
    if (location.pathname === '/dashboard' && !isProjectSelectedInStorage && !isProjectSelected) {
      // Small delay to allow loadRecentDashboard event to set the localStorage
      const timeoutId = setTimeout(() => {
        const stillNotSelected = localStorage.getItem('isProjectSelected') !== 'true';
        if (stillNotSelected) {
          navigate('/');
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    
    // Scroll to top when navigating to dashboard
    if (location.pathname === '/dashboard') {
      window.scrollTo(0, 0);
      // Also scroll the main content area to top
      const mainContent = document.querySelector('.dashboard-content');
      if (mainContent) {
        mainContent.scrollTop = 0;
      }
    }
  }, [location.pathname, isProjectSelected, navigate]);

  // Function to generate shareable link
  const generateShareableLink = () => {
    if (projectData.opportunityNo) {
      const baseUrl = window.location.origin;
      return `${baseUrl}/project/${projectData.opportunityNo}`;
    }
    return null;
  };

  // Add effect to handle metrics refresh and initial loading
  useEffect(() => {
    // Create a reusable function for fetching metrics
    const fetchSchedulingMetrics = async () => {
      if (!projectData?.opportunityNo || !isReady) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/schedules/${projectData.opportunityNo}`,
          await getAuthConfig()
        );
        if (response.data.status === "success" && response.data.data) {
          const data = response.data.data;
          const updatedMetrics = [
            {
              label: "Mobs Used/Contracted",
              value: `${data.mobs_used}/${data.mobs_contracted}`,
              type: "mobs",
              used: data.mobs_used,
              total: data.mobs_contracted,
            },
            {
              label: "Days Used/Estimated",
              value: `${data.days_used}/${data.days_estimated}`,
              type: "days",
              used: data.days_used,
              total: data.days_estimated,
            },
            {
              label: "Trips Used/Estimated",
              value: `${data.trip_used}/${data.trip_estimated}`,
              type: "trips",
              used: data.trip_used,
              total: data.trip_estimated,
            },
          ];
          
          setMetrics(updatedMetrics);
          
          // Dispatch event for other components to listen for
          window.dispatchEvent(new CustomEvent('metricsUpdated', {
            detail: updatedMetrics
          }));
        } else {
          const defaultMetrics = [
            { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
            { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
            { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
          ];
          
          setMetrics(defaultMetrics);
          
          // Dispatch event with default metrics
          window.dispatchEvent(new CustomEvent('metricsUpdated', {
            detail: defaultMetrics
          }));
        }
      } catch (error) {
        const defaultMetrics = [
          { label: "Mobs Used/Contracted", value: "N/A", type: "mobs", used: 0, total: 0 },
          { label: "Days Used/Estimated", value: "N/A", type: "days", used: 0, total: 0 },
          { label: "Trips Used/Estimated", value: "N/A", type: "trips", used: 0, total: 0 },
        ];
        
        setMetrics(defaultMetrics);
        
        // Dispatch event with default metrics on error
        window.dispatchEvent(new CustomEvent('metricsUpdated', {
          detail: defaultMetrics
        }));
      }
    };
    
    // Function to handle refresh metrics event
    const handleRefreshMetrics = () => {
      fetchSchedulingMetrics();
    };
    
    // Add event listener
    window.addEventListener('refreshMetrics', handleRefreshMetrics);
    
    // Call on initial load and when projectData changes
    fetchSchedulingMetrics();
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('refreshMetrics', handleRefreshMetrics);
    };
  }, [projectData, isReady, getAuthConfig]);

  // Only jump to Production Performance when explicitly requested (hash / query param).
  // Do not auto-scroll on every PWA dashboard open — it breaks nested scroll on mobile.
  useEffect(() => {
    const onDashboard =
      location.pathname === '/dashboard' || Boolean(projectId);
    const sectionParam = new URLSearchParams(location.search).get('section');
    const wantsProductionSection =
      location.hash === '#production-performance' ||
      sectionParam === 'production-performance';

    if (!onDashboard || !isProjectSelected || !projectData) {
      return undefined;
    }

    const scrollContainer = document.querySelector('.dashboard-content');

    if (wantsProductionSection) {
      const timer = window.setTimeout(() => scrollToProductionPerformance(), 500);
      return () => window.clearTimeout(timer);
    }

    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }

    const innerScroll = document.querySelector('.scrollable-content');
    if (innerScroll) {
      innerScroll.scrollTop = 0;
    }

    return undefined;
  }, [
    location.pathname,
    location.hash,
    location.search,
    projectId,
    isProjectSelected,
    projectData,
  ]);

  return (
    <div className="page-scale">
      <div className="project-dashboard">
        <MobileHeader 
          onMenuToggle={toggleMobileMenu}
          isMenuOpen={isMobileMenuOpen}
          userProfile={userProfile}
          profileImage={profileImage}
          searchValue=""
          onSearchChange={() => {}}
          onColumnToggle={() => {}}
          onReset={() => {}}
          showColumnButton={false}
          showResetButton={false}
          showGlobalSearch
          onGlobalSearchClick={() => setIsGlobalSearchOpen(true)}
        />
        {isStandalonePwa ? (
          <PwaGlobalSearch
            isOpen={isGlobalSearchOpen}
            onClose={() => setIsGlobalSearchOpen(false)}
          />
        ) : (
          <GlobalSearch
            isOpen={isGlobalSearchOpen}
            onOpen={() => setIsGlobalSearchOpen(true)}
            onClose={() => setIsGlobalSearchOpen(false)}
          />
        )}
        <Header
          onGlobalSearchOpen={() => setIsGlobalSearchOpen(true)}
          isGlobalSearchOpen={isGlobalSearchOpen}
        />
        <main className="dashboard-main">
          {showSidebar && (
            <div className="sidebar-container">
              <Sidebar 
                isCollapsed={isSidebarCollapsed} 
                projectData={projectData}
                isMobileMenuOpen={isMobileMenuOpen}
                onMobileMenuClose={closeMobileMenu}
                onMenuToggle={toggleMobileMenu}
                showPinnedProjectName={namePinned}
              />
            </div>
          )}
          <div className={`dashboard-content ${shouldHideSidebar ? 'full-width' : ''}`}>
            {isContactsPage ? (
              location.pathname === '/contacts/manage' ? (
                <div className="projects-page">
                  <ManageContacts />
                </div>
              ) : (
                <div className="flex flex-col px-4 pt-4 pb-9 mx-auto w-full rounded-xl bg-stone-50 max-md:px-4 max-md:mt-0 max-md:max-w-full">
                  <Contacts />
                </div>
              )
            ) : isAccountsPage ? (
              <div className="projects-page">
                <AccountManagement />
              </div>
            ) : isInventoryPage ? (
              <div className="projects-page">
                <InventoryManagement />
              </div>
            ) : isEquipmentPage ? (
              <div className="projects-page">
                <EquipmentManagement />
              </div>
            ) : isConfigurationPage ? (
              <div className={`flex flex-col px-4 mx-auto w-full rounded-xl bg-stone-50 dark:bg-slate-900 transition-colors duration-300 max-md:px-5 max-md:mt-7 max-md:max-w-full ${isCatalogManagePage ? 'stock-inv-page pt-8 pb-4' : 'pt-4 pb-9'}`}>
                {!isRoleLoaded ? (
                  <div className="config-access-loading">Checking access...</div>
                ) : (
                  <ConfigurationPage />
                )}
              </div>
            ) : location.pathname === '/' ? (
              <div className="projects-page">
                <ManageProject />
              </div>
            ) : location.pathname === '/archive' ? (
              <div className="projects-page">
                <ProjectArchive />
              </div>
            ) : isProjectSelected && projectData ? (
              isTemplateReport ? (
                reportProjectReady ? (
                  <ProjectTemplateReport
                    key={`${reportTemplateId}:${projectData.projectId || projectData.projectNo || projectData.id_project}`}
                    templateId={reportTemplateId}
                    projectData={projectData}
                    preparedBy={userProfile?.displayName || accounts?.[0]?.name || ""}
                  />
                ) : (
                  <div className="px-6 py-10 text-sm text-stone-500">Loading the selected project…</div>
                )
              ) : isDailyReport ? (
                reportProjectReady ? (
                <ElastizellDailyReport
                  key={projectData.projectId || projectData.projectNo || projectData.id_project}
                  projectData={projectData}
                  preparedBy={userProfile?.displayName || accounts?.[0]?.name || ""}
                />
                ) : (
                  <div className="px-6 py-10 text-sm text-stone-500">Loading the selected project…</div>
                )
              ) : (
              <>
                <div className="sticky-project-details" ref={projectDetailsRef}>
                  <ProjectDetails projectData={projectData} />
                </div>
                <div className="scrollable-content">
                  <ScopeDescription scopeDescription={projectData.scopeDescription} idProject={projectData.id_project} />
                  <FinancialPerformance projectData={projectData} />
                  <Scheduling projectData={projectData} />
                  <div id={PRODUCTION_PERFORMANCE_SECTION_ID}>
                    <ProductionPerformance metrics={metrics} milesInfo={milesInfo} />
                  </div>
                  <section className="communication-section">
                    <TeamsChat projectData={projectData} />
                    <ProjectEmails projectData={projectData} />
                  </section>
                  <div className="mt-8">
                    <Contacts />
                  </div>
                </div>
              </>
              )
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
}

export default ProjectDashboard;
